import React, { useState, useMemo } from 'react';
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Alert,
  message,
  Card,
  ConfigProvider,
  Dropdown,
} from 'antd';
import type { MenuProps } from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  DownloadOutlined,
  UserAddOutlined,
  DownOutlined,
} from '@ant-design/icons';
import { SpaceAssignmentTable } from './SpaceAssignmentTable';
import { InviteUserModal } from './InviteUserModal';
import { AssignSpaceAfterRoleChangeModal } from './AssignSpaceAfterRoleChangeModal';
import { TransferOwnershipModal } from './TransferOwnershipModal';
import { DeleteUserModal } from './DeleteUserModal';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_USERS,
  UPDATE_USER,
  DELETE_USER,
  GetUsersQueryResponse,
  UpdateUserMutationVariables,
  UpdateUserMutationResponse,
  DeleteUserMutationVariables,
  DeleteUserMutationResponse,
} from './Users';
import { AUTH_API_URL } from './SpaceAssignments';
import { useAuthUser } from '@/hooks/useAuth';
import { useSpaceAccess } from '@/contexts/SpaceAccessContext';
import { useTimezone } from '@/hooks/useTimezone';

const { Option } = Select;

interface UserTableRecord {
  key: string;
  id: string;
  name: string;
  email: string;
  role: string;
  lastActiveAt?: string;
}

export const UserManagement = () => {
  const { isSuperUser } = useAuthUser();
  const { isAuthApiAdmin, isAuthApiOwner, currentUserId } = useSpaceAccess();
  const timezone = useTimezone();
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editingData, setEditingData] = useState<{ name: string; role: string }>({
    name: '',
    role: '',
  });
  const [searchText, setSearchText] = useState<string>('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState<boolean>(false);
  const [roleChangeUserId, setRoleChangeUserId] = useState<string | null>(null);
  const [transferOwnershipTarget, setTransferOwnershipTarget] = useState<{
    userId: string;
    userName: string;
  } | null>(null);
  const [deleteUserTarget, setDeleteUserTarget] = useState<{
    userId: string;
    userName: string;
  } | null>(null);

  // Fetch users using GraphQL from auth API

  const {
    data: usersData,
    loading: isLoading,
    error,
    refetch,
  } = useQuery<GetUsersQueryResponse>(GET_USERS, {
    variables: { limit: 100, offset: 0 },
    context: {
      uri: AUTH_API_URL,
    },
  });

  const [updateUser, { loading: isUpdating }] = useMutation<
    UpdateUserMutationResponse,
    UpdateUserMutationVariables
  >(UPDATE_USER, {
    context: {
      uri: AUTH_API_URL,
    },
  });
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [deleteUser] = useMutation<DeleteUserMutationResponse, DeleteUserMutationVariables>(
    DELETE_USER,
    {
      context: {
        uri: AUTH_API_URL,
      },
    },
  );

  // Transform GraphQL data to table format, excluding superusers
  const tableData: UserTableRecord[] = useMemo(() => {
    const users = usersData?.users?.users || [];
    return users
      .filter((user) => !user.roles?.some((r) => r.toLowerCase() === 'superuser'))
      .map((user) => ({
        key: user.id,
        id: user.id,
        name: user.name,
        email: user.email,
        role: user.roles?.some((r) => r.toLowerCase() === 'owner')
          ? 'Owner'
          : user.roles?.some((r) => r.toLowerCase() === 'admin')
          ? 'Admin'
          : 'Member',
        lastActiveAt: user.last_login || undefined,
      }));
  }, [usersData]);

  // Filter table data based on search text
  const filteredTableData = useMemo(() => {
    if (!searchText.trim()) return tableData;
    const lowerSearch = searchText.toLowerCase();
    return tableData.filter(
      (user) =>
        user.name?.toLowerCase().includes(lowerSearch) ||
        user.email?.toLowerCase().includes(lowerSearch) ||
        user.role?.toLowerCase().includes(lowerSearch),
    );
  }, [tableData, searchText]);

  // Handle refresh
  const handleRefresh = async () => {
    try {
      await refetch();
      message.success('User list refreshed');
    } catch {
      message.error('Failed to refresh user list');
    }
  };

  // Handle export (mock implementation)
  const handleExport = () => {
    message.info('Export functionality coming soon');
  };

  const handleOpenInviteModal = () => {
    setIsInviteModalOpen(true);
  };

  const handleInviteSuccess = () => {
    setIsInviteModalOpen(false);
    handleRefresh();
  };

  const handleEditUser = (userId: string, name: string, role: string) => {
    setEditingUserId(userId);
    setEditingData({ name, role });
  };

  const handleSaveEdit = async (userId: string) => {
    try {
      if (!editingData.name.trim()) {
        message.error('Name cannot be empty');
        return;
      }

      // Check if this is a role downgrade from Admin/Owner to Member
      const originalUser = tableData.find((u) => u.id === userId);
      const wasAdminOrOwner = originalUser?.role === 'Admin' || originalUser?.role === 'Owner';
      const isNowMember = editingData.role === 'Member';
      const isRoleDowngrade = wasAdminOrOwner && isNowMember;

      const roles =
        editingData.role === 'Owner'
          ? ['owner']
          : editingData.role === 'Admin'
          ? ['admin']
          : ['butlr_user'];
      await updateUser({
        variables: {
          id: userId,
          input: {
            name: editingData.name,
            roles: roles,
          },
        },
      });
      message.success('User updated successfully');
      setEditingUserId(null);
      setEditingData({ name: '', role: '' });
      refetch();

      // If role was downgraded to Member, prompt to assign spaces
      if (isRoleDowngrade) {
        setRoleChangeUserId(userId);
      }
    } catch (err) {
      console.error('Failed to update user:', err);
      message.error('Failed to update user');
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditingData({ name: '', role: '' });
  };

  const handleDeleteUser = async () => {
    if (!deleteUserTarget) return;

    setDeletingUserId(deleteUserTarget.userId);
    try {
      await deleteUser({
        variables: { id: deleteUserTarget.userId },
      });
      message.success('User deleted successfully');
      setDeleteUserTarget(null);
      refetch();
    } catch (err) {
      console.error('Failed to delete user:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete user';
      message.error(errorMessage);
    } finally {
      setDeletingUserId(null);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: '18%',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a || !b) return 0;
        return (a.name || '').localeCompare(b.name || '');
      },
      render: (text: string, record: UserTableRecord) => {
        if (editingUserId === record.key) {
          return (
            <Input
              value={editingData.name}
              onChange={(e) => setEditingData({ ...editingData, name: e.target.value })}
              placeholder="Enter name"
            />
          );
        }
        return text;
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      width: '25%',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a || !b) return 0;
        return (a.email || '').localeCompare(b.email || '');
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: '12%',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a || !b) return 0;
        return (a.role || '').localeCompare(b.role || '');
      },
      render: (text: string, record: UserTableRecord) => {
        if (editingUserId === record.key) {
          return (
            <Select
              value={editingData.role}
              onChange={(value) => setEditingData({ ...editingData, role: value })}
              placeholder="Select a role"
              style={{ width: '100%' }}
              className="[&_.ant-select-selector]:!bg-white"
            >
              <Option value="Member">Member</Option>
              <Option value="Admin">Admin</Option>
              {isSuperUser && <Option value="Owner">Owner</Option>}
            </Select>
          );
        }
        return text;
      },
    },
    {
      title: 'Last Active',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      width: '22%',
      sorter: (a: UserTableRecord, b: UserTableRecord) => {
        if (!a.lastActiveAt && !b.lastActiveAt) return 0;
        if (!a.lastActiveAt) return 1;
        if (!b.lastActiveAt) return -1;
        return new Date(a.lastActiveAt).getTime() - new Date(b.lastActiveAt).getTime();
      },
      render: (text: string | undefined) => {
        if (!text) return '-';
        const date = new Date(text);
        if (isNaN(date.getTime())) return '-';
        const formatted = date.toLocaleString('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: 'short',
          day: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
        });
        return formatted;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: '15%',
      render: (_: any, record: UserTableRecord) => {
        // Role hierarchy gating:
        // - Cannot edit/delete yourself (backend also prevents self-role-change)
        // - Superuser can edit/delete anyone else
        // - Owner can edit/delete Admins and Members, but not other Owners
        // - Admin can edit/delete Admins and Members, but not Owners
        const isSelf = record.id === currentUserId;
        const isHigherRole = record.role === 'Owner' || record.role === 'Superuser';
        const canEdit =
          !isSelf &&
          (isSuperUser ||
            (isAuthApiOwner && !isHigherRole) ||
            (isAuthApiAdmin && !isAuthApiOwner && !isHigherRole));
        const canDelete = canEdit;
        // Transfer ownership: only owners can transfer, and only to lower roles
        const canTransferOwnership = !isSelf && isAuthApiOwner && !isHigherRole;

        if (editingUserId === record.key) {
          return (
            <Space size="middle">
              <Button type="link" onClick={() => handleSaveEdit(record.key)} loading={isUpdating}>
                Save
              </Button>
              <Button type="link" onClick={handleCancelEdit}>
                Cancel
              </Button>
            </Space>
          );
        }

        // Build dropdown menu items
        const menuItems: MenuProps['items'] = [];
        if (canDelete) {
          menuItems.push({
            key: 'delete',
            label: 'Delete User',
            onClick: () => setDeleteUserTarget({ userId: record.id, userName: record.name }),
          });
        }
        if (canTransferOwnership) {
          menuItems.push({
            key: 'transfer',
            label: 'Transfer Ownership',
            onClick: () => setTransferOwnershipTarget({ userId: record.id, userName: record.name }),
          });
        }

        const hasMoreActions = menuItems.length > 0;

        return (
          <Space size="middle">
            {canEdit && (
              <Button
                type="link"
                onClick={() => handleEditUser(record.key, record.name, record.role)}
              >
                Edit
              </Button>
            )}
            {hasMoreActions && (
              <Dropdown
                menu={{ items: menuItems }}
                trigger={['click']}
                disabled={deletingUserId === record.key}
              >
                <Button
                  type="link"
                  loading={deletingUserId === record.key}
                  onClick={(e) => e.preventDefault()}
                >
                  More <DownOutlined />
                </Button>
              </Dropdown>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#24252C',
        },
      }}
    >
      <div className="bg-surface-sunken w-full min-h-screen p-6">
        {/* Team Members Section */}
        <Card>
          {/* Toolbar */}
          <div className="flex justify-between items-center mb-4">
            <div className="font-semibold text-base">Team Members ({filteredTableData.length})</div>
            <div className="flex items-center gap-3">
              <Input
                placeholder="Search users..."
                prefix={<SearchOutlined className="text-content-tertiary" />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 240 }}
                allowClear
              />
              <Button
                type="primary"
                icon={<UserAddOutlined />}
                onClick={handleOpenInviteModal}
                className="!bg-btn-action-bg !text-btn-action-text hover:!bg-btn-action-hover !border-btn-action-bg"
              >
                Invite
              </Button>
              <Button
                type="text"
                icon={<DownloadOutlined />}
                onClick={handleExport}
                title="Export"
              />
              <Button
                type="text"
                icon={<ReloadOutlined />}
                onClick={handleRefresh}
                title="Refresh"
              />
            </div>
          </div>

          {error && (
            <Alert
              message="Error loading users"
              description="Failed to fetch users from the server. Please try again."
              type="error"
              showIcon
              style={{ marginBottom: 16 }}
              action={
                <Button size="small" onClick={() => refetch()}>
                  Retry
                </Button>
              }
            />
          )}
          <Table
            columns={columns}
            dataSource={filteredTableData}
            loading={isLoading}
            showSorterTooltip={false}
            expandable={{
              expandedRowRender: (record: UserTableRecord) => (
                <SpaceAssignmentTable
                  userId={record.id}
                  userRole={record.role}
                  onUpdate={handleRefresh}
                />
              ),
              rowExpandable: (record: UserTableRecord) =>
                record.role !== 'Admin' && record.role !== 'Owner' && record.role !== 'Superuser',
            }}
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
            }}
          />
        </Card>

        {/* Invite User Modal */}
        <InviteUserModal
          open={isInviteModalOpen}
          onCancel={() => setIsInviteModalOpen(false)}
          onSuccess={handleInviteSuccess}
        />

        {/* Assign Space after Admin→Member role change */}
        {roleChangeUserId && (
          <AssignSpaceAfterRoleChangeModal
            open
            userId={roleChangeUserId}
            onCancel={() => setRoleChangeUserId(null)}
            onSuccess={() => {
              setRoleChangeUserId(null);
              handleRefresh();
            }}
          />
        )}

        {/* Transfer Ownership Modal */}
        {transferOwnershipTarget && (
          <TransferOwnershipModal
            open
            userId={transferOwnershipTarget.userId}
            userName={transferOwnershipTarget.userName}
            onCancel={() => setTransferOwnershipTarget(null)}
            onSuccess={() => {
              setTransferOwnershipTarget(null);
              handleRefresh();
            }}
          />
        )}

        {/* Delete User Modal */}
        {deleteUserTarget && (
          <DeleteUserModal
            open
            userName={deleteUserTarget.userName}
            loading={deletingUserId === deleteUserTarget.userId}
            onCancel={() => setDeleteUserTarget(null)}
            onConfirm={handleDeleteUser}
          />
        )}
      </div>
    </ConfigProvider>
  );
};

export default UserManagement;
